import {action, observable, makeObservable} from 'mobx';

type LockParams = {
    locked?: boolean;
    keepFocus?: boolean;
    loaderTimeout?: number;
    withLoader?: boolean;
    navigationOnly?: boolean;
};

export enum UpdateStatusTypes {
    saved = 'saved',
    updating = 'updating',
    none = 'none'
}

const successTimerDelay = 4000;

export class Lock {
    @observable locked: boolean = true;
    @observable navigationBlocked: boolean = true;
    @observable showLoader: boolean = false;
    @observable status: UpdateStatusTypes = UpdateStatusTypes.none;

    timerRef?: NodeJS.Timer | null = null;
    resetLoaderTimeout?: NodeJS.Timer | null = null;
    resetStatusTimer?: NodeJS.Timer | null = null;
    loaderTimeout: number = 0;

    constructor(params: LockParams) {
        makeObservable(this);
        this.loaderTimeout = params.loaderTimeout || 0;
        this.locked = params?.navigationOnly ? false : true;
    }

    @action
    lockAPI = (options: LockParams) => {
        const {keepFocus, withLoader, navigationOnly} = options;
        this.navigationBlocked = true;
        if (navigationOnly) {
            return;
        }
        this.locked = true;
        const activeElement: any = document.activeElement;
        if (!keepFocus && activeElement && activeElement !== document.body) {
            activeElement?.blur?.(); // lose focus to prevent keyboard interactions;
        }
        clearTimeout(this.resetLoaderTimeout);

        if (this.loaderTimeout && !this.timerRef && withLoader) {
            this.timerRef = setTimeout(() => this.toggleLoader(true), this.loaderTimeout);
        }
        if (this.timerRef && !withLoader) {
            this.toggleLoader(false);
        }
    };

    @action
    unlockAPI = () => {
        this.locked = false;
        this.navigationBlocked = false;
        this.resetLoaderTimeout = setTimeout(() => {
            clearTimeout(this.timerRef);
            this.timerRef = null;
            this.toggleLoader(false);
        }, 80);
    };

    @action
    toggleLoader = (flag: boolean) => {
        this.showLoader = flag;
    };

    @action
    toggleStatus = (status: UpdateStatusTypes) => {
        clearTimeout(this.resetStatusTimer);

        this.status = status;

        if (status === UpdateStatusTypes.saved) {
            this.resetStatusTimer = setTimeout(() => {
                this.toggleStatus(UpdateStatusTypes.none);
            }, successTimerDelay);
        }
    };
}

export class API_Locker {
    sessionsSet = new Map<string, Lock>();

    isLock = session => this.getDataBySession(session)?.locked;

    lockSession = (session?: string, options: LockParams = {}) => {
        const sessionData = this.getDataBySession(session);
        if (session) {
            if (sessionData) {
                sessionData.lockAPI(options);
            } else {
                this.sessionsSet.set(session, new Lock(options));
            }
        }
    };

    unlockSession = (session: string) => {
        const sessionData = this.getDataBySession(session);
        sessionData?.unlockAPI();
    };

    getDataBySession = (session?: string) => this.sessionsSet.get(session);
}

export const apiLocker = new API_Locker();
